<template>
    <div class="sms-identity-validation-component">
        <!-- Success message -->
        <div v-if="verified" class="alert alert-success">
            <i class="fa fa-check-circle" aria-hidden="true"></i>
            <strong>{{ $t('phoneVerifiedSuccess') }}</strong>
            {{ $t('phoneVerifiedMessage') }}
        </div>

        <!-- Main form -->
        <div v-else>
            <div class="alert alert-info">
                <i class="fa fa-mobile" aria-hidden="true"></i>
                {{ $t('smsVerificationDescription') }}
            </div>

            <!-- Error message -->
            <div v-if="errorMessage" class="alert alert-danger">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                {{ errorMessage }}
            </div>

            <!-- Phone number display -->
            <div class="form-group">
                <label>{{ $t('numeroTelephone') }}</label>
                <div class="phone-display">
                    <i class="fa fa-phone" aria-hidden="true"></i>
                    <strong>{{ user ? user.mobile_phone : '' }}</strong>
                </div>
                <p class="help-text">
                    {{ $t('smsWillBeSentToThisNumber') }}
                    <router-link :to="{ name: 'profile_update' }">
                        {{ $t('modifierNumero') }}
                    </router-link>
                </p>
            </div>

            <!-- Step 1: Send code -->
            <div v-if="!codeSent" class="send-code-section">
                <button
                    class="btn btn-primary btn-lg btn-block"
                    @click="sendCode"
                    :disabled="loading || !user || !user.mobile_phone"
                >
                    <span v-if="!loading">
                        <i class="fa fa-paper-plane" aria-hidden="true"></i>
                        {{ $t('envoyerCodeSMS') }}
                    </span>
                    <span v-else>
                        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                        {{ $t('envoi') }}...
                    </span>
                </button>
            </div>

            <!-- Step 2: Verify code -->
            <div v-else class="verify-code-section">
                <div class="alert alert-success">
                    <i class="fa fa-check" aria-hidden="true"></i>
                    {{ $t('smsCodeSent') }}
                </div>

                <div class="form-group">
                    <label for="verification-code">
                        {{ $t('entrezCodeRecu') }}
                        <span class="required-field-flag">(*)</span>
                    </label>
                    <input
                        id="verification-code"
                        v-model="verificationCode"
                        type="text"
                        class="form-control code-input"
                        maxlength="6"
                        :placeholder="$t('placeholderCode6Chiffres')"
                        @input="sanitizeCode"
                        @keyup.enter="verifyCode"
                        :disabled="loading"
                    />
                    <p v-if="expiresAt" class="help-text">
                        <i class="fa fa-clock-o" aria-hidden="true"></i>
                        {{ $t('codeExpireIn10Minutes') }}
                    </p>
                    <!-- Countdown message for resending code -->
                    <p v-if="!canResend && resendCountdown > 0" class="help-text resend-countdown">
                        <i class="fa fa-info-circle" aria-hidden="true"></i>
                        {{ $t('renvoyerCodeDans') }} <strong>{{ formattedCountdown }}</strong>
                    </p>
                </div>

                <div class="button-group">
                    <button
                        class="btn btn-success btn-lg"
                        @click="verifyCode"
                        :disabled="loading || verificationCode.length !== 6"
                    >
                        <span v-if="!loading">
                            <i class="fa fa-check-circle" aria-hidden="true"></i>
                            {{ $t('verifierCode') }}
                        </span>
                        <span v-else>
                            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                            {{ $t('verification') }}...
                        </span>
                    </button>

                    <button
                        class="btn btn-default"
                        @click="resendCode"
                        :disabled="loading || !canResend"
                    >
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        {{ $t('renvoyerCode') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { UserApi } from '../../services/api';
import dialogs from '../../services/dialogs.js';

export default {
    name: 'SmsIdentityValidation',
    data() {
        return {
            loading: false,
            codeSent: false,
            verified: false,
            verificationCode: '',
            errorMessage: null,
            expiresAt: null,
            canResend: false,
            resendCountdown: 0,
            resendTimer: null,
            statusRefreshTimer: null,
            autoSendRequested: false,
            autoSendAttempted: false
        };
    },
    computed: {
        ...mapGetters({
            user: 'auth/user',
            config: 'auth/appConfig'
        }),
        formattedCountdown() {
            const minutes = Math.floor(this.resendCountdown / 60);
            const seconds = this.resendCountdown % 60;
            return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
        }
    },
    watch: {
        user() {
            if (this.autoSendRequested && !this.autoSendAttempted) {
                this.attemptAutoSend();
            }
            this.refreshUserStatus();
        }
    },
    methods: {
        extractErrorMessage(error, fallbackKey) {
            const errorData = error && error.response && error.response.data;
            if (errorData) {
                if (errorData.message) {
                    return errorData.message;
                }
                if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    return errorData.errors[0];
                }
                if (errorData.errors && typeof errorData.errors === 'object') {
                    const firstKey = Object.keys(errorData.errors)[0];
                    if (firstKey && Array.isArray(errorData.errors[firstKey]) && errorData.errors[firstKey][0]) {
                        return errorData.errors[firstKey][0];
                    }
                }
            }
            return this.$t(fallbackKey);
        },
        async refreshUserStatus() {
            if (this.verified) {
                return;
            }

            try {
                await this.$store.dispatch('auth/fetchUser');
                if (this.user && (this.user.phone_verified || this.user.phone_verified_at)) {
                    this.verified = true;
                    this.$emit('verified');
                }
            } catch (e) {
                // Silent refresh failures to avoid noisy UI
            }
        },
        attemptAutoSend() {
            if (this.autoSendAttempted || this.verified || this.loading) {
                return;
            }
            if (this.user && this.user.mobile_phone) {
                this.autoSendAttempted = true;
                this.sendCode();
            }
        },
        sanitizeCode() {
            // Only allow digits
            this.verificationCode = this.verificationCode.replace(/\D/g, '');
        },
        async sendCode() {
            if (!this.user || !this.user.mobile_phone) {
                this.errorMessage = this.$t('numeroTelephoneManquant');
                return;
            }

            this.loading = true;
            this.errorMessage = null;

            try {
                const userApi = new UserApi();
                const response = await userApi.sendPhoneVerificationCode();
                const data = response.data || response;

                if (data.success) {
                    this.codeSent = true;
                    this.expiresAt = data.expires_at;
                    
                    // Afficher le toast de notification de succès
                    dialogs.message(
                        this.$t('codeEnvoyeAvecSucces'),
                        {
                            duration: 6,
                            position: 'top-center',
                            estado: 'success'
                        }
                    );
                    
                    this.startResendTimer();
                } else {
                    this.errorMessage = data.message || this.$t('erreurEnvoiCode');
                }
            } catch (error) {
                console.error('Error sending verification code:', error);
                this.errorMessage = this.extractErrorMessage(error, 'erreurEnvoiCode');
            } finally {
                this.loading = false;
            }
        },
        async verifyCode() {
            if (this.verificationCode.length !== 6) {
                this.errorMessage = this.$t('codeDoit6Chiffres');
                return;
            }

            this.loading = true;
            this.errorMessage = null;

            try {
                const userApi = new UserApi();
                const response = await userApi.verifyPhoneCode(this.verificationCode);
                const data = response.data || response;

                if (data.success) {
                    this.verified = true;
                    // Keep auth state in sync using the existing auth module contract
                    if (data.user) {
                        this.$store.commit('auth/AUTH_SET_USER', data.user);
                    }
                    await this.$store.dispatch('auth/fetchUser');
                    
                    // \u00c9mettre l'\u00e9v\u00e9nement pour informer le parent (Profile.vue)
                    this.$emit('verified');

                    // If user was redirected here from a protected action, continue the flow
                    if (this.$router && this.$router.rememberRoute) {
                        const destination = this.$router.rememberRoute;
                        this.$router.rememberRoute = null;
                        this.$router.replace(destination);
                    } else if (this.$route && this.$route.name === 'identity_validation_sms') {
                        this.$router.replace({ name: 'identity_validation' });
                    }
                } else {
                    this.errorMessage = data.message || this.$t('codeInvalide');
                }
            } catch (error) {
                console.error('Error verifying code:', error);
                this.errorMessage = this.extractErrorMessage(error, 'erreurVerificationCode');
            } finally {
                this.loading = false;
            }
        },
        async resendCode() {
            if (!this.canResend) {
                console.log('\u23f0 [SMS] Cannot resend yet - countdown active');
                return;
            }
            
            console.log('\ud83d\udd04 [SMS] Resending code...');
            this.verificationCode = '';
            this.errorMessage = null;
            
            // R\u00e9initialiser l'\u00e9tat
            this.codeSent = false;
            
            // Envoyer un nouveau code
            await this.sendCode();
        },
        startResendTimer() {
            // Allow resend after 5 minutes (300 seconds)
            this.canResend = false;
            this.resendCountdown = 300;

            if (this.resendTimer) {
                clearInterval(this.resendTimer);
            }

            this.resendTimer = setInterval(() => {
                this.resendCountdown--;
                if (this.resendCountdown <= 0) {
                    this.canResend = true;
                    clearInterval(this.resendTimer);
                    this.resendTimer = null;
                }
            }, 1000);
        }
    },
    mounted() {
        // Check if already verified
        if (this.user && this.user.phone_verified) {
            this.verified = true;
        }

        // Auto-send code if coming from redirect and phone exists
        this.autoSendRequested = this.$route.query.auto_send === '1';
        this.attemptAutoSend();

        // Periodically refresh user verification status
        this.statusRefreshTimer = setInterval(() => {
            this.refreshUserStatus();
        }, 15000);
    },
    beforeDestroy() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }
        if (this.statusRefreshTimer) {
            clearInterval(this.statusRefreshTimer);
        }
    }
};
</script>

<style scoped>
.sms-identity-validation-component {
    padding: 1em 0;
}

.phone-display {
    padding: 0.75em 1em;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1.1em;
    margin-bottom: 0.5em;
}

.phone-display i {
    margin-right: 0.5em;
    color: #5cb85c;
}

.help-text {
    font-size: 0.9em;
    color: #666;
    margin-top: 0.5em;
}

.resend-countdown {
    color: #31708f;
    background-color: #d9edf7;
    border: 1px solid #bce8f1;
    border-radius: 4px;
    padding: 0.5em;
    margin-top: 1em;
}

.resend-countdown strong {
    font-family: 'Courier New', monospace;
    font-size: 1.1em;
}

.send-code-section {
    margin-top: 1.5em;
}

.verify-code-section {
    margin-top: 1.5em;
}

.code-input {
    font-size: 1.5em;
    text-align: center;
    letter-spacing: 0.5em;
    font-family: 'Courier New', monospace;
}

.button-group {
    display: flex;
    gap: 1em;
    margin-top: 1.5em;
}

.button-group .btn {
    flex: 1;
}

.alert {
    margin-bottom: 1.5em;
}

.alert i {
    margin-right: 0.5em;
}

.required-field-flag {
    color: #d9534f;
}

@media (max-width: 768px) {
    .button-group {
        flex-direction: column;
    }
    
    .code-input {
        font-size: 1.2em;
    }
}
</style>
